import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search } from 'lucide-react';

const BACKEND = "https://painscout.onrender.com";

const CATEGORY_COLORS = {
  'Finance':       '#22d3ee',
  'Mental Health': '#a78bfa',
  'Career':        '#fb923c',
  'Health':        '#4ade80',
  'Tech':          '#60a5fa',
  'Relationships': '#f472b6',
  'Education':     '#facc15',
  'Home & Life':   '#a3e635',
  'Other':         '#7b7a88',
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery]       = useState('');
  const [problems, setProblems]             = useState([]);
  const [stats, setStats]                   = useState(null);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState(null);

  const categories = ['All', 'Finance', 'Mental Health', 'Career', 'Health', 'Tech', 'Relationships', 'Education'];

  // Fetch stats once on mount
  useEffect(() => {
    fetch(`${BACKEND}/api/stats`)
      .then(r => r.json())
      .then(data => setStats(data))
      .catch(e => console.error('Stats fetch error:', e));
  }, []);

  // Fetch problems whenever category changes
  useEffect(() => {
    setLoading(true);
    setError(null);

    const params = new URLSearchParams({ limit: '100' });
    if (activeCategory !== 'All') params.set('category', activeCategory);

    fetch(`${BACKEND}/api/problems?${params}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(data => { setProblems(data); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [activeCategory]);

  // Client-side search on already-fetched data
  const filteredProblems = useMemo(() => {
    if (!searchQuery) return problems;
    const query = searchQuery.toLowerCase();
    return problems.filter(p =>
      p.reddit.title.toLowerCase().includes(query) ||
      p.reddit.subreddit.toLowerCase().includes(query) ||
      p.reddit.author.toLowerCase().includes(query)
    );
  }, [problems, searchQuery]);

  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'Critical': return 'bg-[#ef4444] text-white';
      case 'High':     return 'bg-[#fbbf24] text-[#09090e]';
      case 'Medium':   return 'bg-[#60a5fa] text-white';
      default:         return 'bg-[#7b7a88] text-white';
    }
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return '—';
    const hours = Math.floor((new Date() - new Date(timestamp)) / (1000 * 60 * 60));
    if (hours < 1)  return 'just now';
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  // Live stats from API or zeros while loading
  const s = stats || { posts_seen: 0, problems_found: 0, critical: 0, high_severity: 0, finance: 0, mental_health: 0 };

  return (
    <div className="min-h-screen bg-[#09090e] text-[#f0eff6]">
      {/* Topbar */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#09090e] border-b border-[#22222e]">
        <div className="flex items-center justify-between px-8 py-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-3">
              <span className="text-[#ff4500] text-2xl">⬤</span>
              <span className="font-bold text-xl" style={{ fontFamily: 'Syne, sans-serif' }}>PainScout</span>
            </div>
            <span className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>PROBLEM INTELLIGENCE</span>
          </div>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#4ade80] rounded-full animate-pulse"></div>
              <span className="text-[#4ade80] text-sm font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>LIVE</span>
            </div>
            <Link to="/" className="text-[#7b7a88] hover:text-white transition-colors" style={{ fontFamily: 'DM Mono, monospace' }} data-testid="back-to-home-link">
              ← Back to Home
            </Link>
          </div>
        </div>

        {/* Stats Bar */}
        <div className="border-t border-[#22222e] px-8 py-4 bg-[#17171c]">
          <div className="grid grid-cols-2 md:grid-cols-6 gap-6">
            <div data-testid="stat-posts-seen">
              <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                {stats ? s.posts_seen.toLocaleString() : '—'}
              </div>
              <div className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>POSTS SEEN</div>
            </div>
            <div data-testid="stat-problems-found">
              <div className="text-2xl font-bold text-[#ff4500]" style={{ fontFamily: 'Syne, sans-serif' }}>
                {stats ? s.problems_found : '—'}
              </div>
              <div className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>PROBLEMS FOUND</div>
            </div>
            <div data-testid="stat-critical">
              <div className="text-2xl font-bold text-[#ef4444]" style={{ fontFamily: 'Syne, sans-serif' }}>
                {stats ? s.critical : '—'}
              </div>
              <div className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>CRITICAL</div>
            </div>
            <div data-testid="stat-high-severity">
              <div className="text-2xl font-bold text-[#fbbf24]" style={{ fontFamily: 'Syne, sans-serif' }}>
                {stats ? s.high_severity : '—'}
              </div>
              <div className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>HIGH SEVERITY</div>
            </div>
            <div data-testid="stat-finance">
              <div className="text-2xl font-bold text-[#22d3ee]" style={{ fontFamily: 'Syne, sans-serif' }}>
                {stats ? s.finance : '—'}
              </div>
              <div className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>FINANCE</div>
            </div>
            <div data-testid="stat-mental-health">
              <div className="text-2xl font-bold text-[#a78bfa]" style={{ fontFamily: 'Syne, sans-serif' }}>
                {stats ? s.mental_health : '—'}
              </div>
              <div className="text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>MENTAL HEALTH</div>
            </div>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="border-t border-[#22222e] px-8 py-4 flex flex-wrap items-center gap-4">
          <span className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>CATEGORY:</span>
          <div className="flex flex-wrap gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-[#ff4500] text-white'
                    : 'bg-[#17171c] border border-[#22222e] hover:border-[#ff4500]'
                }`}
                style={{
                  fontFamily: 'DM Mono, monospace',
                  color: activeCategory !== cat && cat !== 'All' ? CATEGORY_COLORS[cat] : undefined
                }}
                data-testid={`filter-${cat.toLowerCase().replace(' ', '-')}`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="ml-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#7b7a88] w-4 h-4" />
            <input
              type="text"
              placeholder="Search problems..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 bg-[#17171c] border border-[#22222e] rounded-lg text-sm focus:outline-none focus:border-[#ff4500] transition-colors"
              style={{ fontFamily: 'DM Mono, monospace' }}
              data-testid="search-input"
            />
          </div>
        </div>
      </div>

      {/* Problem Cards Grid */}
      <div className="pt-64 px-8 pb-20">
        <div className="max-w-7xl mx-auto">

          {/* Loading state */}
          {loading && (
            <div className="flex items-center justify-center py-32 gap-3 text-[#7b7a88]"
              style={{ fontFamily: 'DM Mono, monospace' }}>
              <div className="w-2 h-2 bg-[#ff4500] rounded-full animate-bounce" style={{ animationDelay: '0ms' }}/>
              <div className="w-2 h-2 bg-[#ff4500] rounded-full animate-bounce" style={{ animationDelay: '150ms' }}/>
              <div className="w-2 h-2 bg-[#ff4500] rounded-full animate-bounce" style={{ animationDelay: '300ms' }}/>
              <span className="ml-2">Loading problems...</span>
            </div>
          )}

          {/* Error state */}
          {!loading && error && (
            <div className="text-center py-32">
              <div className="text-[#ef4444] text-lg mb-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                ❌ {error}
              </div>
              <p className="text-[#7b7a88] text-sm mt-2">
                Make sure the backend is running:&nbsp;
                <code className="text-[#ff4500]">uvicorn server:app --reload --port 8000</code>
              </p>
            </div>
          )}

          {/* Empty state */}
          {!loading && !error && filteredProblems.length === 0 && (
            <div className="text-center py-32 text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
              <div className="text-4xl mb-4 opacity-20">⬡</div>
              <p>No problems found{activeCategory !== 'All' ? ` in ${activeCategory}` : ''}.</p>
              <p className="text-sm mt-2 opacity-60">Problems appear here once the Nova agent processes them.</p>
            </div>
          )}

          {/* Cards */}
          {!loading && !error && filteredProblems.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProblems.map((problem, index) => {
                const categoryColor = CATEGORY_COLORS[problem.reddit.category] || '#7b7a88';
                return (
                  <div
                    key={problem.post_id}
                    onClick={() => navigate(`/problem/${problem.post_id}`)}
                    className="bg-[#17171c] border border-[#22222e] rounded-xl overflow-hidden hover:border-[#ff4500] hover:translate-y-[-4px] transition-all duration-300 cursor-pointer problem-card-stagger"
                    style={{
                      borderLeft: `3px solid ${categoryColor}`,
                      animationDelay: `${index * 0.1}s`
                    }}
                    data-testid={`problem-card-${problem.post_id}`}
                  >
                    <div className="p-6">
                      {/* Top Row */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-1 bg-[#ff4500]/20 text-[#ff4500] rounded text-xs font-bold" style={{ fontFamily: 'DM Mono, monospace' }}>
                            r/{problem.reddit.subreddit}
                          </span>
                          <span className="px-2 py-1 bg-[#22222e] rounded text-xs" style={{ color: categoryColor, fontFamily: 'DM Mono, monospace' }}>
                            {problem.reddit.category}
                          </span>
                          <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(problem.nova.severity)}`} style={{ fontFamily: 'DM Mono, monospace' }}>
                            {problem.nova.severity}
                          </span>
                        </div>
                        <span className="text-[#7b7a88] text-xs" style={{ fontFamily: 'DM Mono, monospace' }}>
                          {getTimeAgo(problem.reddit.posted_at)}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-white font-bold text-lg mb-3 line-clamp-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                        {problem.reddit.title}
                      </h3>

                      {/* Body Preview */}
                      <p className="text-[#7b7a88] text-sm mb-4 line-clamp-2" style={{ fontFamily: 'DM Mono, monospace' }}>
                        {problem.reddit.body}
                      </p>

                      {/* Bottom Row */}
                      <div className="flex items-center justify-between text-xs text-[#7b7a88]" style={{ fontFamily: 'DM Mono, monospace' }}>
                        <div className="flex items-center gap-4">
                          <span>⬆ {problem.reddit.score}</span>
                          <span>💬 {problem.reddit.num_comments}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#4ade80]">{problem.nova.confidence}%</span>
                          <span className="px-2 py-0.5 bg-[#ff4500]/20 text-[#ff4500] rounded">Nova</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default Dashboard;